// src/components/Apagar.js
// Tela de EXCLUSÃO — busca produto por ID, exibe detalhes e confirma deleção

import React, { useState } from "react";
import { getProdutoById, deleteProduto } from "../services/api";

export default function Apagar({ onVoltar }) {
  // ── Estados ──────────────────────────────────────────────────────
  const [idBusca, setIdBusca]         = useState("");
  const [produto, setProduto]         = useState(null);   // produto encontrado
  const [confirmando, setConfirmando] = useState(false);  // aguardando confirmação
  const [loading, setLoading]         = useState(false);
  const [mensagem, setMensagem]       = useState(null);   // { tipo, texto }
  const [deletado, setDeletado]       = useState(false);  // produto já foi deletado

  // ── Formatação de moeda ──────────────────────────────────────────
  const formatarPreco = (v) =>
    new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v);

  // ── Passo 1: Busca o produto ─────────────────────────────────────
  async function buscarProduto() {
    if (!idBusca || isNaN(idBusca)) {
      setMensagem({ tipo: "erro", texto: "Digite um ID numérico válido." });
      return;
    }
    try {
      setLoading(true);
      setMensagem(null);
      setProduto(null);
      setConfirmando(false);
      setDeletado(false);

      const data = await getProdutoById(idBusca);
      setProduto(data);
    } catch (e) {
      setMensagem({ tipo: "erro", texto: `Produto com ID ${idBusca} não encontrado.` });
    } finally {
      setLoading(false);
    }
  }

  // ── Passo 2: Executar a exclusão após confirmação ────────────────
  async function confirmarExclusao() {
    try {
      setLoading(true);
      await deleteProduto(produto.id);
      setDeletado(true);
      setConfirmando(false);
      setMensagem({
        tipo: "sucesso",
        texto: `Produto "${produto.nome}" (ID: ${produto.id}) excluído com sucesso!`,
      });
      setProduto(null);
      setIdBusca("");
    } catch (e) {
      setMensagem({ tipo: "erro", texto: "Erro ao excluir o produto." });
    } finally {
      setLoading(false);
    }
  }

  // ── Render ───────────────────────────────────────────────────────
  return (
    <div className="tela">
      <div className="tela-header">
        <h2>🗑️ Apagar Produto</h2>
        <p>Busque um produto pelo ID para excluí-lo permanentemente</p>
      </div>

      {/* Aviso de ação irreversível */}
      <div className="alerta-aviso">
        ⚠️ <strong>Atenção:</strong> A exclusão é permanente e não pode ser desfeita.
      </div>

      {/* Busca por ID */}
      <div className="busca-id-container">
        <label>Informe o ID do produto a excluir:</label>
        <div className="busca-id-row">
          <input
            type="number"
            min="1"
            value={idBusca}
            onChange={(e) => { setIdBusca(e.target.value); setDeletado(false); }}
            onKeyDown={(e) => e.key === "Enter" && buscarProduto()}
            placeholder="Ex: 1"
            className="input-id"
          />
          <button
            onClick={buscarProduto}
            className="btn btn-secondary"
            disabled={loading}
          >
            {loading && !confirmando ? "⏳ Buscando..." : "🔍 Buscar"}
          </button>
        </div>
      </div>

      {/* Mensagens */}
      {mensagem && (
        <div className={`status-msg ${mensagem.tipo}`}>
          {mensagem.tipo === "sucesso" ? "✅" : "❌"} {mensagem.texto}
          <button className="btn-fechar-msg" onClick={() => setMensagem(null)}>✕</button>
        </div>
      )}

      {/* Card com dados do produto encontrado */}
      {produto && !deletado && (
        <div className="produto-card-exclusao">
          <div className="produto-card-exclusao__header">
            📦 Produto encontrado
          </div>
          <div className="produto-card-exclusao__dados">
            <div className="dado-item">
              <span className="dado-label">ID</span>
              <span className="dado-valor">#{produto.id}</span>
            </div>
            <div className="dado-item">
              <span className="dado-label">Nome</span>
              <span className="dado-valor">{produto.nome}</span>
            </div>
            <div className="dado-item">
              <span className="dado-label">Preço</span>
              <span className="dado-valor">{formatarPreco(produto.preco)}</span>
            </div>
            <div className="dado-item">
              <span className="dado-label">Estoque</span>
              <span className="dado-valor">{produto.estoque} unidades</span>
            </div>
          </div>

          {/* Botões de ação */}
          {!confirmando ? (
            // Primeiro clique: pede confirmação
            <div className="form-acoes">
              <button
                className="btn btn-danger"
                onClick={() => setConfirmando(true)}
              >
                🗑️ Excluir este produto
              </button>
              <button
                className="btn btn-ghost"
                onClick={() => { setProduto(null); setIdBusca(""); }}
              >
                Cancelar
              </button>
            </div>
          ) : (
            // Segundo clique: confirmação final (dupla proteção)
            <div className="confirmacao-exclusao">
              <p>Tem certeza que deseja excluir <strong>"{produto.nome}"</strong>?</p>
              <div className="form-acoes">
                <button
                  className="btn btn-danger"
                  onClick={confirmarExclusao}
                  disabled={loading}
                >
                  {loading ? "⏳ Excluindo..." : "✅ Sim, excluir definitivamente"}
                </button>
                <button
                  className="btn btn-ghost"
                  onClick={() => setConfirmando(false)}
                  disabled={loading}
                >
                  ❌ Não, cancelar
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
